import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  Alert,
  ScrollView,
  Pressable,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Text, Surface, Button, ActivityIndicator } from 'react-native-paper';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LoadingImage from '../components/LoadingImage';
import { RootStackParamList } from '../types/navigation';
import helpService, { HelpRequest } from '../services/helpService';
import SlidingMenu from '../components/SlidingMenu';
import MaxWidthContainer from '../components/MaxWidthContainer';
import { brandPrimary, brandPrimarySoft, brandButton } from '../theme/paperTheme';

type HelpFeedScreenNavigationProp = NativeStackNavigationProp<RootStackParamList, 'HelpFeed'>;

type HelpFeedScreenProps = {
  navigation: HelpFeedScreenNavigationProp;
};

type Category = {
  id: string;
  icon: string;
  description: string;
  iconBg: string;
  cardBg: string;
  webGradient: string;
};

const categories: Category[] = [
  {
    id: 'Classroom Management',
    icon: '📚',
    description: 'Build routines, improve behavior, and create positive learning environments.',
    iconBg: '#DBEAFE',
    cardBg: '#F8FBFF',
    webGradient: 'linear-gradient(145deg, #EFF6FF 0%, #FFFFFF 72%)',
  },
  {
    id: 'Curriculum & Instruction',
    icon: '🍎',
    description: 'Plan engaging lessons and strengthen instructional strategies.',
    iconBg: '#D1FAE5',
    cardBg: '#F6FDF9',
    webGradient: 'linear-gradient(145deg, #ECFDF5 0%, #FFFFFF 72%)',
  },
  {
    id: 'Assessment & Evaluation',
    icon: '📝',
    description: 'Design assessments and measure student learning effectively.',
    iconBg: '#E0E7FF',
    cardBg: '#F8F9FF',
    webGradient: 'linear-gradient(145deg, #EEF2FF 0%, #FFFFFF 72%)',
  },
  {
    id: 'Technology Integration',
    icon: '💻',
    description: 'Incorporate digital tools to enhance teaching and learning.',
    iconBg: '#CFFAFE',
    cardBg: '#F5FCFD',
    webGradient: 'linear-gradient(145deg, #ECFEFF 0%, #FFFFFF 72%)',
  },
  {
    id: 'Special Education',
    icon: '🌟',
    description: 'Support diverse learning needs with inclusive strategies.',
    iconBg: '#FEF3C7',
    cardBg: '#FFFBF5',
    webGradient: 'linear-gradient(145deg, #FFFBEB 0%, #FFFFFF 72%)',
  },
  {
    id: 'Family Communication',
    icon: '💬',
    description: 'Strengthen partnerships with families through effective communication.',
    iconBg: '#FCE7F3',
    cardBg: '#FFF8FB',
    webGradient: 'linear-gradient(145deg, #FDF2F8 0%, #FFFFFF 72%)',
  },
  {
    id: 'Professional Growth',
    icon: '🌱',
    description: 'Explore ideas for reflection, leadership, and career development.',
    iconBg: '#D1FAE5',
    cardBg: '#F4FBF7',
    webGradient: 'linear-gradient(145deg, #F0FDF4 0%, #FFFFFF 72%)',
  },
  {
    id: 'Other Topics',
    icon: '✨',
    description: 'Ask about anything else related to education.',
    iconBg: '#E5E7EB',
    cardBg: '#FAFAFA',
    webGradient: 'linear-gradient(145deg, #F3F4F6 0%, #FFFFFF 72%)',
  },
];

type CategoryCardProps = {
  category: Category;
  isDesktop: boolean;
  onPress: () => void;
};

const CategoryCard: React.FC<CategoryCardProps> = ({ category, isDesktop, onPress }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={category.id}
      style={({ pressed }) => [
        styles.categoryCard,
        isDesktop ? styles.categoryCardDesktop : styles.categoryCardMobile,
        { backgroundColor: category.cardBg },
        Platform.OS === 'web' && ({ backgroundImage: category.webGradient } as object),
        (hovered || pressed) && styles.categoryCardActive,
        hovered && Platform.OS === 'web' && styles.categoryCardHovered,
      ]}
    >
      <View style={[styles.iconCircle, { backgroundColor: category.iconBg }]}>
        <Text style={styles.categoryIcon}>{category.icon}</Text>
      </View>
      <Text style={styles.categoryTitle}>{category.id}</Text>
      <Text style={styles.categoryDescription}>{category.description}</Text>
    </Pressable>
  );
};

const HelpFeedScreen: React.FC<HelpFeedScreenProps> = ({ navigation }) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  const [activeFilter, setActiveFilter] = useState<'all' | 'recent' | 'popular'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [helpRequests, setHelpRequests] = useState<HelpRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [responseCounts, setResponseCounts] = useState<Record<string, number>>({});
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  useEffect(() => {
    if (selectedCategory) {
      fetchHelpRequests();
    }
  }, [activeFilter, selectedCategory]);

  const fetchHelpRequests = async () => {
    try {
      setIsLoading(true);
      let status: 'open' | 'resolved' | undefined;

      if (activeFilter === 'recent') {
        status = 'resolved';
      } else if (activeFilter === 'all') {
        status = 'open';
      }

      const requests = await helpService.getHelpRequests(
        status === 'resolved' ? 'recent' : 'pending',
        false,
      );

      const filteredRequests = selectedCategory
        ? requests.filter((request) => request.category === selectedCategory)
        : requests;

      setHelpRequests(filteredRequests);

      const counts: Record<string, number> = {};
      for (const request of filteredRequests) {
        try {
          const responses = await helpService.getResponses(request._id);
          counts[request._id] = responses.length;
        } catch (error) {
          console.warn(`Failed to fetch responses for request ${request._id}:`, error);
          counts[request._id] = 0;
        }
      }
      setResponseCounts(counts);
    } catch (error) {
      console.error('Error fetching help requests:', error);
      Alert.alert('Error', 'Failed to fetch help requests. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setHelpRequests([]);
  };

  const handleRespond = (helpRequest: HelpRequest) => {
    navigation.navigate('HelpResponse', { helpRequest });
  };

  const toggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const renderHelpRequest = ({ item }: { item: HelpRequest }) => (
    <Surface style={styles.helpRequestCard} elevation={1}>
      <Text style={styles.requestTitle}>{item.title}</Text>
      <Text style={styles.requestDescription}>{item.description}</Text>
      <View style={styles.requestMeta}>
        <Text style={styles.requestAuthor}>{item.user.displayName}</Text>
        <Text style={styles.requestDate}>{new Date(item.createdAt).toLocaleDateString()}</Text>
      </View>
      <View style={styles.requestFooter}>
        <Text style={styles.requestCategory}>{item.category}</Text>
        <View style={styles.responseInfo}>
          <Text style={styles.responseCount}>
            {responseCounts[item._id] || 0}{' '}
            {responseCounts[item._id] === 1 ? 'response' : 'responses'}
          </Text>
          <Button
            mode="contained"
            onPress={() => handleRespond(item)}
            style={styles.respondButton}
            buttonColor={brandButton}
            textColor="#ffffff"
          >
            Respond
          </Button>
        </View>
      </View>
    </Surface>
  );

  return (
    <View style={styles.container}>
      <Surface style={styles.surface}>
        <View style={styles.headerContainer}>
          <TouchableOpacity
            style={styles.logoContainer}
            onPress={() => navigation.navigate('Home')}
          >
            <LoadingImage
              source={require('../../assets/logo-transparent.png')}
              style={styles.logo}
              contentFit="contain"
              transition={200}
              cachePolicy="memory-disk"
            />
          </TouchableOpacity>
          <View style={styles.headerRight}>
            {selectedCategory && (
              <Button
                mode="text"
                onPress={handleBackToCategories}
                style={styles.backButton}
                textColor={brandPrimary}
              >
                ← Categories
              </Button>
            )}
            <TouchableOpacity style={styles.menuButton} onPress={toggleMenu}>
              <Text style={styles.menuText}>☰</Text>
            </TouchableOpacity>
          </View>
        </View>

        <MaxWidthContainer style={isDesktop ? styles.wideContainer : undefined}>
          {!selectedCategory ? (
            <ScrollView
              contentContainerStyle={styles.categoriesContainer}
              showsVerticalScrollIndicator={false}
            >
              <View style={styles.heroSection}>
                <Text style={styles.headline}>Ready to lend a hand?</Text>
                <Text style={styles.subheading}>Choose the area you can help with!</Text>
              </View>

              <View style={[styles.categoriesGrid, isDesktop && styles.categoriesGridDesktop]}>
                {categories.map((category) => (
                  <CategoryCard
                    key={category.id}
                    category={category}
                    isDesktop={isDesktop}
                    onPress={() => handleCategorySelect(category.id)}
                  />
                ))}
              </View>
            </ScrollView>
          ) : (
            <>
              <View style={styles.categoryBanner}>
                <Text style={styles.categoryBannerLabel}>Helping with</Text>
                <Text style={styles.categoryBannerTitle}>{selectedCategory}</Text>
              </View>

              <View style={styles.filterContainer}>
                <TouchableOpacity
                  style={[styles.filterPill, activeFilter === 'all' && styles.filterPillActive]}
                  onPress={() => setActiveFilter('all')}
                >
                  <Text
                    style={[styles.filterText, activeFilter === 'all' && styles.filterTextActive]}
                  >
                    Open requests
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.filterPill, activeFilter === 'recent' && styles.filterPillActive]}
                  onPress={() => setActiveFilter('recent')}
                >
                  <Text
                    style={[
                      styles.filterText,
                      activeFilter === 'recent' && styles.filterTextActive,
                    ]}
                  >
                    Resolved
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[
                    styles.filterPill,
                    activeFilter === 'popular' && styles.filterPillActive,
                  ]}
                  onPress={() => setActiveFilter('popular')}
                >
                  <Text
                    style={[
                      styles.filterText,
                      activeFilter === 'popular' && styles.filterTextActive,
                    ]}
                  >
                    Popular
                  </Text>
                </TouchableOpacity>
              </View>

              {isLoading ? (
                <View style={styles.loadingContainer}>
                  <ActivityIndicator size="large" color={brandPrimary} />
                  <Text style={styles.loadingText}>Loading help requests…</Text>
                </View>
              ) : helpRequests.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <Text style={styles.emptyEmoji}>🌿</Text>
                  <Text style={styles.emptyTitle}>No requests here yet</Text>
                  <Text style={styles.emptyText}>
                    Check back soon — a fellow educator may need your support in this area.
                  </Text>
                </View>
              ) : (
                <FlatList
                  data={helpRequests}
                  renderItem={renderHelpRequest}
                  keyExtractor={(item) => item._id}
                  contentContainerStyle={styles.listContainer}
                  showsVerticalScrollIndicator={false}
                />
              )}
            </>
          )}
        </MaxWidthContainer>
      </Surface>

      <SlidingMenu isVisible={isMenuVisible} onClose={toggleMenu} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F4F7FB',
  },
  surface: {
    flex: 1,
    elevation: 0,
    backgroundColor: '#F4F7FB',
  },
  wideContainer: {
    maxWidth: 960,
  },
  headerContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 20,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 0,
    ...(Platform.OS === 'web'
      ? {
          boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)',
        }
      : {
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(15, 23, 42, 0.06)',
        }),
  },
  logoContainer: {
    width: 72,
    height: 72,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: '100%',
    height: '100%',
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  backButton: {
    marginRight: 4,
  },
  menuButton: {
    width: 52,
    height: 52,
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuText: {
    fontSize: 36,
    color: '#1E293B',
  },
  categoriesContainer: {
    paddingHorizontal: 20,
    paddingTop: 32,
    paddingBottom: 48,
  },
  heroSection: {
    marginBottom: 36,
    paddingHorizontal: 4,
  },
  headline: {
    fontSize: 32,
    fontWeight: '700',
    color: '#1E293B',
    textAlign: 'center',
    letterSpacing: -0.5,
    marginBottom: 12,
    lineHeight: 40,
  },
  subheading: {
    fontSize: 17,
    fontWeight: '400',
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 26,
    maxWidth: 480,
    alignSelf: 'center',
  },
  categoriesGrid: {
    gap: 16,
  },
  categoriesGridDesktop: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 20,
  },
  categoryCard: {
    borderRadius: 18,
    paddingVertical: 28,
    paddingHorizontal: 24,
    alignItems: 'flex-start',
    minHeight: 200,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
    ...(Platform.OS === 'web'
      ? {
          boxShadow: '0 4px 20px rgba(15, 23, 42, 0.06)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer',
        }
      : {
          shadowColor: '#0F172A',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.06,
          shadowRadius: 12,
          elevation: 3,
        }),
  },
  categoryCardMobile: {
    width: '100%',
  },
  categoryCardDesktop: {
    width: '48%',
  },
  categoryCardActive: {
    opacity: 0.96,
  },
  categoryCardHovered: Platform.select({
    web: {
      transform: [{ translateY: -4 }],
      boxShadow: '0 12px 32px rgba(15, 118, 110, 0.14)',
      borderColor: 'rgba(15, 118, 110, 0.2)',
    },
    default: {},
  }),
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  categoryIcon: {
    fontSize: 28,
  },
  categoryTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    lineHeight: 24,
  },
  categoryDescription: {
    fontSize: 14,
    color: '#475569',
    lineHeight: 22,
  },
  categoryBanner: {
    marginHorizontal: 20,
    marginTop: 24,
    marginBottom: 8,
    padding: 20,
    borderRadius: 16,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
  },
  categoryBannerLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: brandPrimary,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  categoryBannerTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1E293B',
  },
  filterContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  filterPill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.08)',
  },
  filterPillActive: {
    backgroundColor: brandButton,
    borderColor: brandButton,
  },
  filterText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  filterTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  listContainer: {
    paddingHorizontal: 20,
    paddingBottom: 32,
    gap: 16,
  },
  helpRequestCard: {
    backgroundColor: '#FFFFFF',
    padding: 20,
    marginBottom: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
  },
  requestTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    lineHeight: 26,
  },
  requestDescription: {
    fontSize: 15,
    color: '#475569',
    marginBottom: 14,
    lineHeight: 22,
  },
  requestMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  requestAuthor: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
  },
  requestDate: {
    fontSize: 13,
    color: '#94A3B8',
  },
  requestFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  requestCategory: {
    fontSize: 12,
    fontWeight: '600',
    color: brandPrimary,
    backgroundColor: brandPrimarySoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    overflow: 'hidden',
  },
  responseInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  responseCount: {
    fontSize: 14,
    color: '#64748B',
  },
  respondButton: {
    borderRadius: 999,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 64,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    marginHorizontal: 20,
    marginTop: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.06)',
  },
  emptyEmoji: {
    fontSize: 40,
    marginBottom: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyText: {
    fontSize: 15,
    textAlign: 'center',
    color: '#64748B',
    lineHeight: 22,
    maxWidth: 320,
  },
});

export default HelpFeedScreen;
